import { useEffect, useState } from "react";
import { Button, Input, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import FacebookIcon from "../../icons/FacebookIcon";
import InstagramIcon from "../../icons/InstagramIcon";
import TwitterXIcon from "../../icons/TwitterXIcon";
import YextApi, { ensureHttps, yextErrorMessage } from "../../../api/YextApi";
import "./ProfileLinksSection.scss";

type SocialKey = "facebook" | "instagram" | "twitter";

const SOCIAL_FIELDS: { key: SocialKey; label: string; Icon: () => JSX.Element }[] = [
  { key: "facebook", label: "Facebook", Icon: FacebookIcon },
  { key: "instagram", label: "Instagram", Icon: InstagramIcon },
  { key: "twitter", label: "Twitter", Icon: TwitterXIcon },
];

interface LinksForm {
  onlineOrdering: string[];
  reservations: string[];
  menu: string[];
  social: Record<SocialKey, string>;
  giftCard: string[];
}

const EMPTY: LinksForm = {
  onlineOrdering: [""],
  reservations: [""],
  menu: [""],
  social: { facebook: "", instagram: "", twitter: "" },
  giftCard: [""],
};

type RepeatableKey = "onlineOrdering" | "reservations" | "menu" | "giftCard";

interface Props {
  locationId?: number;
}

export function ProfileLinksSection({ locationId }: Props) {
  const [form, setForm] = useState<LinksForm>(EMPTY);
  const [savedSnapshot, setSavedSnapshot] = useState<LinksForm>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      const links = data.links;
      if (!links) return;
      // Repeatable groups show at least one empty input when there are no saved values.
      const orEmpty = (arr?: string[]) => (arr && arr.length ? arr : [""]);
      const loaded: LinksForm = {
        onlineOrdering: orEmpty(links.onlineOrdering),
        reservations: orEmpty(links.reservations),
        menu: orEmpty(links.menu),
        social: {
          facebook: links.social?.facebook ?? "",
          instagram: links.social?.instagram ?? "",
          twitter: links.social?.twitter ?? "",
        },
        giftCard: orEmpty(links.giftCard),
      };
      setForm(loaded);
      setSavedSnapshot(loaded);
    });
  }, [locationId]);

  function setRepeatable(field: RepeatableKey, idx: number, value: string) {
    setForm((f) => ({
      ...f,
      [field]: f[field].map((l, i) => (i === idx ? value : l)),
    }));
  }

  function addRepeatable(field: RepeatableKey) {
    setForm((f) => ({ ...f, [field]: [...f[field], ""] }));
  }

  function setSocial(key: SocialKey, value: string) {
    setForm((f) => ({ ...f, social: { ...f.social, [key]: value } }));
  }

  function discardChanges() {
    setForm(savedSnapshot);
  }

  async function saveChanges() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      await YextApi.syncLocation(locationId, {
        links: {
          onlineOrdering: form.onlineOrdering.filter(Boolean),
          reservations: form.reservations.filter(Boolean),
          menu: form.menu.filter(Boolean),
          social: form.social,
          giftCard: form.giftCard.filter(Boolean),
        },
      });
      setSavedSnapshot(form);
      message.success("Links saved.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save links."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-links">
      <h3 className="pl-title">Links</h3>

      <RepeatableLinkGroup
        label="Online Ordering"
        placeholder="Online Ordering Link"
        values={form.onlineOrdering}
        onChange={(idx, v) => setRepeatable("onlineOrdering", idx, v)}
        onAdd={() => addRepeatable("onlineOrdering")}
      />

      <RepeatableLinkGroup
        label="Reservations"
        placeholder="Reservation Link"
        values={form.reservations}
        onChange={(idx, v) => setRepeatable("reservations", idx, v)}
        onAdd={() => addRepeatable("reservations")}
      />

      <RepeatableLinkGroup
        label="Menu"
        placeholder="Menu Link"
        values={form.menu}
        onChange={(idx, v) => setRepeatable("menu", idx, v)}
        onAdd={() => addRepeatable("menu")}
      />

      <div className="pl-field-group">
        <span className="pl-field-label">Social Media</span>
        <div className="pl-social-list">
          {SOCIAL_FIELDS.map(({ key, label, Icon }) => (
            <div key={key} className="pl-social-row">
              <span className="pl-social-icon" aria-hidden>
                <Icon />
              </span>
              <Input
                placeholder={label}
                size="large"
                value={form.social[key]}
                onChange={(e) => setSocial(key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <RepeatableLinkGroup
        label="Gift Card"
        placeholder="Gift Card Link"
        values={form.giftCard}
        onChange={(idx, v) => setRepeatable("giftCard", idx, v)}
        onAdd={() => addRepeatable("giftCard")}
      />

      <footer className="pl-actions">
        <Button className="pl-discard-btn" size="large" onClick={discardChanges} disabled={saving}>
          Discard Changes
        </Button>
        <Button
          type="primary"
          danger
          className="pl-save-btn"
          size="large"
          onClick={saveChanges}
          loading={saving}
        >
          Save Changes
        </Button>
      </footer>
    </div>
  );
}

interface RepeatableLinkGroupProps {
  label: string;
  placeholder: string;
  values: string[];
  onChange: (idx: number, value: string) => void;
  onAdd: () => void;
}

function RepeatableLinkGroup({
  label,
  placeholder,
  values,
  onChange,
  onAdd,
}: RepeatableLinkGroupProps) {
  return (
    <div className="pl-field-group">
      <span className="pl-field-label">{label}</span>
      <div className="pl-link-list">
        {values.map((v, idx) => (
          <Input
            key={idx}
            placeholder={placeholder}
            size="large"
            value={v}
            onChange={(e) => onChange(idx, e.target.value)}
            onBlur={(e) => onChange(idx, ensureHttps(e.target.value))}
          />
        ))}
        <Button
          size="small"
          className="pl-add-link-btn"
          icon={<PlusOutlined />}
          onClick={onAdd}
        >
          Add Link
        </Button>
      </div>
    </div>
  );
}
